/* Pizzeria Bros franchise landing page
   - bilingual FR/EN swap (no page reload, no duplicate DOM)
   - scroll reveals + word-by-word tagline (IntersectionObserver only)
   - client-side validation, honeypot, UTM/source capture
   ENDPOINT: see FORM_MODE below. Install notes ship with the package. */

(function () {
  'use strict';

  // 'native'   the browser posts the form itself. The Shopify build uses this:
  //            {% form 'contact' %} needs a real POST to /contact so Shopify
  //            sends the notification email and redirects back with
  //            form.posted_successfully?.
  // 'preview'  no back end wired. Validate, confirm, log, send nothing.
  // 'endpoint' POST via fetch to FORM_ENDPOINT (Formspree, Basin, a Worker).
  var FORM_MODE     = 'native';
  var FORM_ENDPOINT = '';           // used only when FORM_MODE === 'endpoint'
  var FORM_METHOD   = 'POST';

  // SILAS lead relay (Cloudflare Worker, SILAS-owned). Mirrors the lead into
  // Mailchimp so SILAS sees enquiries. Shopify's own notification email is
  // unchanged and remains the instant alert. This call never gates the visitor.
  var LEAD_RELAY_URL = 'https://silas-lead-relay.silasstudios.workers.dev';
  var LEAD_PENDING_KEY = 'silas_lead_pending';
  var LEAD_TTL_MS      = 30 * 60 * 1000;   // parked lead is never kept longer than this

  /* ---------------- i18n ---------------- */
  var STORE = 'pb-franchise-lang';
  var nodes = [];
  // The locale the host page was rendered in, read before apply() rewrites it.
  // On Shopify this is the store locale: /fr/ renders fr, the default path renders en.
  var THEME_LANG = (document.documentElement.getAttribute('lang') || '').slice(0, 2).toLowerCase();

  function collect() {
    nodes = Array.prototype.slice.call(document.querySelectorAll('[data-en][data-fr]'));
    nodes.forEach(function (n) {
      if (n.hasAttribute('placeholder')) { n.dataset.phEn = n.getAttribute('placeholder'); }
    });
  }

  function apply(lang) {
    document.documentElement.lang = lang === 'fr' ? 'fr-CA' : 'en';
    nodes.forEach(function (n) {
      var v = n.dataset[lang];
      if (v == null) return;
      if (n.tagName === 'INPUT' || n.tagName === 'TEXTAREA') { n.setAttribute('placeholder', v); }
      else if (n.tagName === 'META') { n.setAttribute('content', v); }
      else if (n.tagName === 'TITLE') { n.textContent = v; }
      else { n.innerHTML = v; }
    });
    // Screen-reader attributes: landmark labels and image descriptions.
    document.querySelectorAll('[data-en-aria][data-fr-aria]').forEach(function (n) {
      n.setAttribute('aria-label', n.dataset[lang + 'Aria']);
    });
    document.querySelectorAll('[data-en-alt][data-fr-alt]').forEach(function (n) {
      n.setAttribute('alt', n.dataset[lang + 'Alt']);
    });
    document.querySelectorAll('.langsw__btn').forEach(function (b) {
      var on = b.dataset.lang === lang;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', String(on));
    });
    var hidden = document.querySelector('[data-track="page_language"]');
    if (hidden) hidden.value = lang;
    buildTaglineWords();
    try { localStorage.setItem(STORE, lang); } catch (e) {}
  }

  function initialLang() {
    // ?lang= always wins, so either version can be linked or tested directly.
    var q = new URLSearchParams(location.search).get('lang');
    if (q === 'fr' || q === 'en') return q;

    // Shopify build: no in-page switch, the store's own locale is authoritative.
    // /fr/... is the French storefront, the unprefixed path is English. The theme
    // chrome follows that locale, so the body copy has to as well.
    if (!document.querySelector('.langsw__btn')) {
      if (/^\/fr(\/|$)/.test(location.pathname)) return 'fr';
      if (/^\/en(\/|$)/.test(location.pathname)) return 'en';
      if (THEME_LANG === 'fr' || THEME_LANG === 'en') return THEME_LANG;
      return 'fr';
    }

    // Standalone build: the page carries its own switch, so remember the choice.
    if (/^\/en(\/|$)/.test(location.pathname)) return 'en';
    try { var s = localStorage.getItem(STORE); if (s === 'en' || s === 'fr') return s; } catch (e) {}
    return 'fr';
  }

  /* ---------------- scroll reveal ---------------- */
  function reveals() {
    var els = document.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (e) { e.classList.add('on'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('on'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.1 });
    els.forEach(function (e) { io.observe(e); });
  }

  /* ---------------- tagline, word by word ---------------- */
  var wordIO = null;
  function buildTaglineWords() {
    var el = document.querySelector('[data-reveal-words]');
    if (!el) return;
    if (wordIO) { wordIO.disconnect(); wordIO = null; }
    var text = el.textContent.replace(/\s+/g, ' ').trim();
    el.textContent = '';
    text.split(' ').forEach(function (w, i) {
      var s = document.createElement('span');
      s.className = 'w';
      s.textContent = w;
      s.style.transitionDelay = (i * 55) + 'ms';
      el.appendChild(s);
      el.appendChild(document.createTextNode(' '));
    });
    var words = el.querySelectorAll('.w');
    if (!('IntersectionObserver' in window)) {
      words.forEach(function (w) { w.classList.add('on'); });
      return;
    }
    wordIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('on'); wordIO.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -35% 0px', threshold: 0.5 });
    words.forEach(function (w) { wordIO.observe(w); });
  }

  /* ---------------- form ---------------- */
  var MSG = {
    required: { en: 'This field is required.', fr: 'Ce champ est obligatoire.' },
    email:    { en: 'Enter a valid email address.', fr: 'Entrez une adresse courriel valide.' },
    phone:    { en: 'Enter a phone number we can reach you on.', fr: 'Entrez un numéro de téléphone où vous joindre.' },
    capital:  { en: 'Please choose yes or no.', fr: 'Veuillez choisir oui ou non.' },
    role:     { en: 'Please choose one.', fr: 'Veuillez en choisir une.' },
    consent:  { en: 'Please tick the box so we may contact you.', fr: 'Veuillez cocher la case pour que nous puissions vous joindre.' },
    sending:  { en: 'Sending…', fr: 'Envoi en cours…' }
  };
  function t(key) {
    var lang = document.documentElement.lang.indexOf('fr') === 0 ? 'fr' : 'en';
    return MSG[key][lang];
  }
  function setErr(field, msg) {
    if (!field) return;
    field.classList.toggle('is-bad', !!msg);
    var p = field.querySelector('[data-err]');
    if (p) p.textContent = msg || '';
  }

  function stamp(form) {
    var p = new URLSearchParams(location.search);
    var set = function (n, v) { var el = form.querySelector('[data-track="' + n + '"]'); if (el) el.value = v; };
    set('utm_source', p.get('utm_source') || '');
    set('utm_medium', p.get('utm_medium') || '');
    set('utm_campaign', p.get('utm_campaign') || '');
    set('utm_content', p.get('utm_content') || '');
    set('utm_term', p.get('utm_term') || '');
    set('fbclid', p.get('fbclid') || '');
    set('referrer', document.referrer || 'direct');
    set('landing_page', location.origin + location.pathname);
    set('submitted_at', new Date().toISOString());
    var src = p.get('utm_source') || p.get('source');
    if (!src) {
      var r = document.referrer;
      src = !r ? 'direct'
          : /google\./.test(r) ? 'google'
          : /facebook\.|instagram\./.test(r) ? 'meta'
          : /pizzeriabros\.ca/.test(r) ? 'pizzeriabros.ca'
          : 'referral';
    }
    set('source', src);
  }

  function validate(form) {
    var ok = true, first = null;
    function fail(field) { ok = false; if (!first) first = field; }

    ['f_first', 'f_last', 'f_cityprov', 'f_territory', 'f_timeline', 'f_exp'].forEach(function (id) {
      var el = document.getElementById(id), f = el.closest('.field');
      if (!el.value.trim()) { setErr(f, t('required')); fail(el); } else { setErr(f, ''); }
    });

    var em = document.getElementById('f_email');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(em.value.trim())) { setErr(em.closest('.field'), t('email')); fail(em); }
    else { setErr(em.closest('.field'), ''); }

    var ph = document.getElementById('f_phone');
    if (ph.value.replace(/\D/g, '').length < 10) { setErr(ph.closest('.field'), t('phone')); fail(ph); }
    else { setErr(ph.closest('.field'), ''); }

    var cap = form.querySelector('[data-role="capital"]:checked');
    var capF = document.getElementById('capitalField');
    if (!cap) { setErr(capF, t('capital')); fail(capF.querySelector('input')); } else { setErr(capF, ''); }

    var role = form.querySelector('[data-role="ownerrole"]:checked');
    var roleF = document.getElementById('roleField');
    if (!role) { setErr(roleF, t('role')); fail(roleF.querySelector('input')); } else { setErr(roleF, ''); }

    var con = form.querySelector('[data-role="consent"]');
    var conErr = form.querySelector('[data-err="consent"]');
    if (!con.checked) { if (conErr) conErr.textContent = t('consent'); fail(con); }
    else if (conErr) { conErr.textContent = ''; }

    if (first) first.focus();
    return ok;
  }


  /* ---------------- Meta pixel: franchise Lead, consent gated ----------------
     Dataset 4346120225605044, owned by the SILAS Studios portfolio and shared
     with ad account 411014911526419 (Pizzeria Bros Canada).

     Nothing is loaded and no request reaches Meta until the visitor accepts
     marketing consent through Shopify's Customer Privacy API. Someone who
     declines, or who has not answered the banner, is never tracked. Quebec
     Law 25 requires that, and the store's own pixel already behaves this way.

     trackSingle addresses our pixel by id, so the store's commerce pixel on
     the same page never receives our events. */
  var PIXEL_ID   = '4346120225605044';
  var pixelUp    = false;
  var leadFired  = false;
  var leadQueued = false;

  function marketingOk() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      return !!(cp && cp.marketingAllowed && cp.marketingAllowed());
    } catch (e) { return false; }
  }

  function loadFbq() {
    if (window.fbq) { return; }
    !function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v;
      s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  }

  function startPixel() {
    if (pixelUp || !marketingOk()) { return; }
    loadFbq();
    pixelUp = true;
    window.fbq('init', PIXEL_ID);
    window.fbq('trackSingle', PIXEL_ID, 'PageView');
    if (leadQueued) { leadQueued = false; fireLead(); }
  }

  function fireLead() {
    if (leadFired) { return; }
    // A lead that happens before consent is held, not dropped: if the visitor
    // accepts afterwards it is sent then, and if they never do it is discarded
    // with the page.
    if (!pixelUp) { leadQueued = true; return; }
    leadFired = true;
    window.fbq('trackSingle', PIXEL_ID, 'Lead', {
      content_name:     'Franchise Fleur de Lys',
      content_category: 'franchise-enquiry'
    });
  }

  function watchConsent() {
    startPixel();                                            // consent already on file
    document.addEventListener('visitorConsentCollected', startPixel);
    try {
      if (window.Shopify && window.Shopify.loadFeatures) {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function () { startPixel(); }
        );
      }
    } catch (e) { /* no consent API means no tracking, which is the safe default */ }
  }

  // Native Shopify posts and redirects, so success arrives as a fresh page
  // load with #formOk rendered visible. Endpoint/preview mode calls
  // fireLead() directly from done(true) instead.
  // Retry a parked lead. Runs on the page Shopify redirects back to, where no
  // navigation is pending, so a retry can actually finish. Three attempts with
  // growing gaps. On success, or once past the TTL, the copy is removed.
  function retryPendingLead() {
    var raw;
    try { raw = sessionStorage.getItem(LEAD_PENDING_KEY); } catch (e) { return; }
    if (!raw) { return; }

    var rec;
    try { rec = JSON.parse(raw); } catch (e) {
      try { sessionStorage.removeItem(LEAD_PENDING_KEY); } catch (e2) {}
      return;
    }
    if (!rec || !rec.p || !rec.p.email || !rec.t || (Date.now() - rec.t) > LEAD_TTL_MS) {
      try { sessionStorage.removeItem(LEAD_PENDING_KEY); } catch (e) {}
      return;
    }

    var delays = [0, 2000, 6000];
    function attempt(n) {
      if (n >= delays.length) { return; }   // keep the copy; a later page view retries
      setTimeout(function () {
        fetch(LEAD_RELAY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(rec.p),
          mode: 'cors'
        }).then(function (r) {
          if (r && r.ok) { try { sessionStorage.removeItem(LEAD_PENDING_KEY); } catch (e) {} }
          else { attempt(n + 1); }
        }).catch(function () { attempt(n + 1); });
      }, delays[n]);
    }
    attempt(0);
  }

  function leadOnLoad() {
    // A parked lead is retried on EVERY load. The old gate required #formOk to
    // be visible when this ran, and that moment does not reliably happen, so
    // parked leads were never sent. Retrying always is safe: the relay upserts
    // by email, and the parked copy is removed as soon as one send succeeds.
    retryPendingLead();

    // The pixel Lead event still belongs to the success page only, because
    // that is the point where Shopify has accepted the submission.
    var ok = document.getElementById('formOk');
    if (ok && !ok.hidden) { fireLead(); }
  }

  function initForm() {
    var form = document.getElementById('franchiseForm');
    if (!form) return;
    stamp(form);

    // Answering "No" to the capital question ends the flow politely instead
    // of submitting.
    var capNo = document.getElementById('capitalNo');
    var submitBtn = form.querySelector('button[type="submit"]');
    var footNote = form.querySelector('.form__foot');
    var consentRow = form.querySelector('.consent');
    form.querySelectorAll('[data-role="capital"]').forEach(function (r) {
      r.addEventListener('change', function () {
        var declined = form.querySelector('[data-role="capital"]:checked').value === 'No';
        capNo.hidden = !declined;
        submitBtn.hidden = declined;
        if (footNote) footNote.hidden = declined;
        if (consentRow) consentRow.hidden = declined;
        if (declined) {
          setErr(document.getElementById('capitalField'), '');
          capNo.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      });
    });

    var terr = document.getElementById('f_territory');
    var cityWrap = document.getElementById('cityWrap');
    terr.addEventListener('change', function () {
      var need = /other|Ontario|Elsewhere|Somewhere/i.test(terr.value) || terr.value === 'Other';
      cityWrap.hidden = !need;
    });

    form.addEventListener('submit', function (ev) {
      if (form.querySelector('[data-role="hp"]').value) { ev.preventDefault(); return; }  // honeypot
      if (!validate(form)) { ev.preventDefault(); return; }
      var capAns = form.querySelector('[data-role="capital"]:checked');
      if (capAns && capAns.value === 'No') { ev.preventDefault(); capNo.hidden = false; return; }
      stamp(form);

      // Native mode hands the POST back to the browser. The hidden
      // lead-source fields are stamped above, before it goes.
      // Mirror to the SILAS relay before the browser navigates away.
      // keepalive lets the request outlive the page. Failures are swallowed:
      // a relay problem must never cost a real lead.
      try {
        var relay = {};
        new FormData(form).forEach(function (v, k) {
          var m = k.match(/^contact\[(.+)\]$/);
          if (m) { relay[m[1]] = v; }
        });
        if (relay.email) {
          // Park a copy first. The send below happens as the browser is already
          // navigating, so it gets one chance and no retry. If anything on the
          // device stops it, the parked copy is retried on the success page,
          // where nothing is in flight. Cleared on success, and never kept
          // longer than LEAD_TTL_MS.
          // Park a copy first, so a failed send has a second chance on the
          // next page load.
          try {
            sessionStorage.setItem(LEAD_PENDING_KEY, JSON.stringify({ t: Date.now(), p: relay }));
          } catch (e) { /* private mode: the send below is then the only path */ }

          // Then send NOW. keepalive lets the request outlive this page as the
          // browser navigates away, which is what keepalive is for.
          //
          // This used to wait for a "success page" that visitors do not
          // reliably reach: Shopify posts to /fr/contact, where this script is
          // not loaded. Every lead was parked and never sent. Sending here can
          // record a submission Shopify later refuses, which is a small
          // reporting error. Never sending loses the customer.
          fetch(LEAD_RELAY_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(relay),
            keepalive: true,
            mode: 'cors'
          }).then(function (r) {
            // Clear the park as soon as the relay confirms. A failed or
            // blocked send leaves it parked for retryPendingLead().
            if (r && r.ok) {
              try { sessionStorage.removeItem(LEAD_PENDING_KEY); } catch (e) {}
            }
          }).catch(function () {});
        }
      } catch (e) { /* relay must never block a real lead */ }

      if (FORM_MODE === 'native') { return; }
      ev.preventDefault();

      var btn = form.querySelector('button[type="submit"]');
      var label = btn.innerHTML;
      btn.disabled = true; btn.textContent = t('sending');
      document.getElementById('formFail').hidden = true;

      var done = function (good) {
        btn.disabled = false; btn.innerHTML = label;
        if (good) {
          form.querySelectorAll('.field, .form__row, .consent, .form__foot, button[type=submit]')
              .forEach(function (n) { n.hidden = true; });
          document.getElementById('formOk').hidden = false;
          document.getElementById('formOk').scrollIntoView({ block: 'center' });
          fireLead();
        } else {
          document.getElementById('formFail').hidden = false;
        }
      };

      if (FORM_MODE !== 'endpoint' || !FORM_ENDPOINT) {   // preview build, nothing wired yet
        console.info('[franchise] preview mode, payload:', Object.fromEntries(new FormData(form)));
        setTimeout(function () { done(true); }, 600);
        return;
      }

      fetch(FORM_ENDPOINT, {
        method: FORM_METHOD,
        body: new FormData(form),
        headers: { Accept: 'application/json' }
      }).then(function (r) { done(r.ok); }).catch(function () { done(false); });
    });
  }

  /* ---------------- boot ---------------- */
  document.documentElement.classList.remove('no-js');
  collect();
  apply(initialLang());
  document.querySelectorAll('.langsw__btn').forEach(function (b) {
    b.addEventListener('click', function () { apply(b.dataset.lang); });
  });
  reveals();
  initForm();
  watchConsent();
  leadOnLoad();
})();
